import { BlueprintMinimalInfo } from "../api/Blueprints";
import { REPORT_TYPES } from "../api/Reports";
import {
  isCombinedFullNameWithinDbLimit,
  isValidProfileUrl,
  type NameParts,
} from "../utils/personFieldLimits";

export type WizardChecktypeSelection = {
  selectedMisconductChecktype: string;
  selectedThreeSixtyChecktype: string;
  selectedBehaviorKit: string;
  requisitionNumber: string;
  checkedPulseReport: boolean;
  pulseReportMonths: number;
  internalQa?: boolean;
  clientQa?: boolean;
};

export type ChecktypeCompanyContext = {
  internal_qa?: boolean;
  client_qa?: boolean;
  blueprintsInfo?: BlueprintMinimalInfo[] | null;
};

export type EligibleReportFlags = {
  iqa: boolean;
  cqa: boolean;
  includeRecur: boolean;
};

export {
  buildCombinedFullName,
  DB_VARCHAR_255,
  isCombinedFullNameWithinDbLimit,
  isValidProfileUrl,
} from "../utils/personFieldLimits";
export type { NameParts } from "../utils/personFieldLimits";

export type RerunParentReportTypeHint = {
  is_360?: boolean;
  report_type?: string;
};

/** Prefer company-scoped blueprints; fall back to the global list when empty or missing.

``mutateAsync`` for company blueprints can resolve before ``blueprintsList`` loads and
return ``[]``. Nullish coalescing alone would keep that empty array and block the fallback.
*/
export function resolveRerunBlueprintList(
  companyBlueprintsInfo: BlueprintMinimalInfo[] | undefined | null,
  globalBlueprintsList: BlueprintMinimalInfo[] | undefined | null
): BlueprintMinimalInfo[] {
  if (companyBlueprintsInfo && companyBlueprintsInfo.length > 0) {
    return companyBlueprintsInfo;
  }
  return globalBlueprintsList ?? [];
}

/** Resolve report type for a rerun parent blueprint (list lookup, then parent hints). */
export function resolveRerunBlueprintReportType(
  blueprintUuid: string,
  blueprints: BlueprintMinimalInfo[],
  parentReport?: RerunParentReportTypeHint
): REPORT_TYPES | undefined {
  const fromBlueprintList = blueprints.find(
    (bp) => bp.blueprint_uuid === blueprintUuid
  )?.report_type;
  if (fromBlueprintList) {
    return fromBlueprintList;
  }

  if (
    parentReport?.is_360 ||
    parentReport?.report_type === REPORT_TYPES.THREE_SIXTY ||
    parentReport?.report_type === "360"
  ) {
    return REPORT_TYPES.THREE_SIXTY;
  }

  if (parentReport?.report_type === REPORT_TYPES.MISCONDUCT) {
    return REPORT_TYPES.MISCONDUCT;
  }

  return undefined;
}

/** Map a parent report blueprint to wizard check-type selection fields. */
export function buildRerunChecktypeSelections(
  blueprintUuid: string,
  reportType?: REPORT_TYPES
): {
  selectedMisconductChecktype: string;
  selectedThreeSixtyChecktype: string;
} {
  if (!blueprintUuid) {
    return {
      selectedMisconductChecktype: "",
      selectedThreeSixtyChecktype: "",
    };
  }

  if (reportType === REPORT_TYPES.THREE_SIXTY) {
    return {
      selectedMisconductChecktype: "",
      selectedThreeSixtyChecktype: blueprintUuid,
    };
  }

  return {
    selectedMisconductChecktype: blueprintUuid,
    selectedThreeSixtyChecktype: "",
  };
}

/** Keep a kit the new company still offers. Otherwise use the first id, which callers sort as the company default. Undefined ids mean the kit list is still loading. */
export function resolveBehaviorKitId(
  selectedBehaviorKit: string,
  behaviorKitIds: string[] | undefined
): string {
  if (!behaviorKitIds || behaviorKitIds.length === 0) {
    return selectedBehaviorKit;
  }
  if (selectedBehaviorKit && behaviorKitIds.includes(selectedBehaviorKit)) {
    return selectedBehaviorKit;
  }
  return behaviorKitIds[0];
}

/**
 * Apply check-type settings for a company.
 * The first company seeds IQA/CQA from that company's flags.
 * A later company change clears check type, Pulse, and IQA/CQA so the previous
 * company's selections are not restored when the user switches back.
 */
export function reconcileChecktypeForCompany(
  previous: WizardChecktypeSelection,
  company: ChecktypeCompanyContext,
  behaviorKitIds?: string[],
  options?: { initialCompany?: boolean }
): WizardChecktypeSelection {
  const selectedBehaviorKit = resolveBehaviorKitId(
    previous.selectedBehaviorKit,
    behaviorKitIds
  );

  if (options?.initialCompany) {
    return {
      ...previous,
      selectedBehaviorKit,
      internalQa: !!company.internal_qa,
      clientQa: !!company.client_qa,
    };
  }

  return {
    ...previous,
    selectedMisconductChecktype: "",
    selectedThreeSixtyChecktype: "",
    selectedBehaviorKit,
    internalQa: false,
    clientQa: false,
    checkedPulseReport: false,
    pulseReportMonths: 0,
  };
}

/** Drop misconduct-only settings when the user selects a 360 check. */
export function checktypeAfterThreeSixtySelection(
  previous: WizardChecktypeSelection,
  blueprintUuid: string
): WizardChecktypeSelection {
  return {
    ...previous,
    selectedMisconductChecktype: "",
    selectedThreeSixtyChecktype: blueprintUuid,
    internalQa: false,
    clientQa: false,
    checkedPulseReport: false,
    pulseReportMonths: 0,
  };
}

/** Payload flags follow eligibility, so a stored checkbox cannot be submitted after its control is hidden. */
export function eligibleReportFlags(
  checktype: WizardChecktypeSelection,
  company: ChecktypeCompanyContext | null | undefined
): EligibleReportFlags {
  const misconductSelected = !!checktype.selectedMisconductChecktype;
  return {
    iqa: misconductSelected && !!company?.internal_qa && !!checktype.internalQa,
    cqa: misconductSelected && !!company?.client_qa && !!checktype.clientQa,
    includeRecur: misconductSelected && !!checktype.checkedPulseReport,
  };
}

export function sanitizeInternationalWithDashesAndSpaces(
  input: string,
  maxLength: number = 255
): string {
  const sanitized = input.replace(/[^\p{L}0-9-\s]/gu, "");
  return sanitized.slice(0, maxLength);
}

export function textWithMaxLength(
  text: string,
  maxLength: number = 255
): string {
  return text.slice(0, maxLength);
}

export function atLeastOneLetter(input: string): boolean {
  return /\p{L}/u.test(input);
}

// Names: allow unicode letters (with marks), spaces, dot, apostrophe, hyphen
export function sanitizeName(input: string, maxLength: number = 255): string {
  const removeNotAllowed = /[^\p{L}\p{M} .'-]/gu;
  return input.replace(removeNotAllowed, "").slice(0, maxLength);
}

export function isNameValid(input: string): boolean {
  if (!input) return false;
  if (!atLeastOneLetter(input)) return false;
  return sanitizeName(input) === input;
}

// Aliases: allow unicode letters (with marks), digits, spaces, dot, apostrophe, hyphen, underscore, at
export function sanitizeAlias(input: string, maxLength = 255): string {
  const removeNotAllowed = /[^\p{L}\p{M}0-9 .'_@-]/gu;
  return input.replace(removeNotAllowed, "").slice(0, maxLength);
}

export function isAliasValid(input: string): boolean {
  if (!input) return false;
  if (!atLeastOneLetter(input)) return false;
  return sanitizeAlias(input) === input;
}

export function isWizardNameWithinDbLimit(parts: NameParts): boolean {
  return isCombinedFullNameWithinDbLimit(parts);
}

/** First social URL row may be left blank; other rows must be valid DB-safe URLs. */
export function isWizardProfileUrlValid(url: string, idx: number): boolean {
  return isValidProfileUrl(url, { allowEmpty: idx === 0 });
}
